/**
 * 家长端 · 统计仪表盘（见契约 1.4）。
 *
 * 内容：完成率、连续天数（streak）、总投入时长、本周趋势；趋势折线（完成率 / 时长 / 积分，
 * 按天周月切换）；打卡热力图（GitHub 风格一年视图）；目标进度树（含量化目标完成度）；
 * 明细导出 CSV。
 *
 * ⚠️ 周报/月报、徽章、微信提醒不在本批范围（属于阶段 4）。
 *
 * 两处刻意的设计：
 *  1. **导出双通道**：优先调后端 `/stats/export`；后端未就绪时前端用已加载的数据
 *     自己拼一份 UTF-8 BOM 的 CSV，保证"导出"这个按钮永远有反应。
 *  2. 完成率必然解释得清：数字旁边永远给出"计划 N 次 / 完成 M 次"，
 *     否则家长会怀疑这个百分比是怎么算出来的。
 */
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { NODE_STATUS_LABEL, describePointsRule, formatDuration, type NodeStatus } from '@fht/shared';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState, ProgressBar, StatTile } from '../../components/ui/Bits';
import { TimeSeriesChart } from '../../components/stats/TimeSeriesChart';
import { Heatmap } from '../../components/stats/Heatmap';
import {
  statsApi,
  useHeatmap,
  useStatsNodes,
  useStatsOverview,
  useTimeseries,
  type TimeseriesInterval,
  type TimeseriesMetric,
} from '../../lib/domainQueries';
import { useStudent } from '../../lib/queries';
import { toErrorMessage } from '../../lib/api/error';
import { cn } from '../../lib/utils';
import type { NodeSummary } from '../../lib/api/types';

const METRIC_LABEL: Record<TimeseriesMetric, string> = {
  completion: '完成率',
  duration: '投入时长',
  points: '获得积分',
};

const INTERVAL_LABEL: Record<TimeseriesInterval, string> = {
  day: '按天',
  week: '按周',
  month: '按月',
};

/**
 * 状态 → 色调。这里必须覆盖**四个**状态（`Record<NodeStatus, …>` 强制），
 * 因为归档不再是状态了（见 `node.archivedAt`），"已归档 / 进行中"那种二选一
 * 的老写法在新模型下既编译不过、也表达不了"已归档 + 已完成"。
 */
const NODE_STATUS_TONE: Record<NodeStatus, 'neutral' | 'brand' | 'success' | 'warn'> = {
  active: 'success',
  paused: 'neutral',
  completed: 'brand',
  stopped: 'warn',
};

/** 目标进度树的一行（含量化目标完成度）。 */
function ProgressNodeRow({ node, depth = 1 }: { node: NodeSummary; depth?: number }) {
  const progress = node.progress ?? null;
  const children = node.children ?? [];

  return (
    <li>
      <div className="rounded-xl border border-slate-200 p-3" style={{ marginLeft: (depth - 1) * 16 }}>
        <div className="flex flex-wrap items-center gap-2">
          <span aria-hidden="true">🎯</span>
          <span className="font-medium text-slate-900">{node.name}</span>
          <Badge tone={NODE_STATUS_TONE[node.status]}>{NODE_STATUS_LABEL[node.status]}</Badge>
          {/* 归档是独立维度：已归档就额外挂一个标记，而不是替换掉状态标签 */}
          {node.archivedAt !== null && <Badge tone="neutral">已归档</Badge>}
          {node.isLongTerm && <Badge tone="brand">长期</Badge>}
        </div>

        {progress && (progress.targetValue > 0 || progress.doneValue > 0) ? (
          <ProgressBar
            className="mt-2 max-w-sm"
            ratio={progress.ratio}
            done={progress.doneValue}
            {...(progress.targetValue > 0 ? { target: progress.targetValue } : {})}
          />
        ) : (
          <p className="mt-1 text-xs text-slate-400">
            {node.isLongTerm ? '长期目标：只累计不显示百分比' : '还没有量化目标'}
          </p>
        )}

        {(node.habits?.length ?? 0) > 0 && (
          <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
            {(node.habits ?? []).map((habit) => (
              <li key={habit.id}>
                🔁 {habit.name}
                {habit.targetValue !== null && ` · 每次 ${habit.targetValue}${habit.unit ?? ''}`}
                {` · ${describePointsRule({
                  pointsPerUnit: habit.pointsPerUnit,
                  unitSize: habit.pointsUnitSize,
                  targetValue: habit.targetValue,
                  unit: habit.unit,
                  dailyPointsCap: habit.dailyPointsCap,
                })}`}
              </li>
            ))}
          </ul>
        )}
      </div>

      {children.length > 0 && (
        <ul className="mt-2 space-y-2">
          {children.map((child) => (
            <ProgressNodeRow key={child.id} node={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** 前端兜底 CSV：后端导出未就绪时用已加载的树拼一份可用的明细。 */
function buildFallbackCsv(nodes: NodeSummary[], nickname: string): string {
  const rows: string[][] = [['层级', '类型', '名称', '开始日期', '截止日期', '已完成', '目标', '完成度']];
  const walk = (list: NodeSummary[], depth: number) => {
    for (const node of list) {
      const progress = node.progress ?? null;
      rows.push([
        String(depth),
        '目标',
        node.name,
        node.startDate,
        node.isLongTerm ? '长期' : (node.endDate ?? ''),
        String(progress?.doneValue ?? 0),
        String(progress?.targetValue ?? 0),
        progress?.ratio === null || progress?.ratio === undefined
          ? ''
          : `${Math.round(progress.ratio * 100)}%`,
      ]);
      if (node.children) walk(node.children, depth + 1);
    }
  };
  walk(nodes, 1);
  // 加 BOM，Excel 打开才不会把中文显示成乱码
  return `\uFEFF${rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\r\n')}\r\n# 导出对象：${nickname}\r\n`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function ParentStatsPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const studentQuery = useStudent(id);
  const nickname = studentQuery.data?.nickname ?? '孩子';

  const [metric, setMetric] = useState<TimeseriesMetric>('completion');
  const [interval, setInterval] = useState<TimeseriesInterval>('day');
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  const overviewQuery = useStatsOverview(id);
  const seriesQuery = useTimeseries(id, metric, interval);
  const nodesQuery = useStatsNodes(id);
  const heatmapQuery = useHeatmap(id, year);

  const overview = overviewQuery.data?.overview;
  const series = seriesQuery.data?.series.points ?? [];
  const nodes = nodesQuery.data?.nodes ?? [];
  const heatmap = heatmapQuery.data?.heatmap ?? { year, days: [] };

  const allNotReady = useMemo(
    () =>
      (overviewQuery.data?.notReady ?? false) &&
      (seriesQuery.data?.notReady ?? false) &&
      (nodesQuery.data?.notReady ?? false) &&
      (heatmapQuery.data?.notReady ?? false),
    [overviewQuery.data, seriesQuery.data, nodesQuery.data, heatmapQuery.data],
  );

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { filename, blob } = await statsApi.downloadExport(id, 'csv');
      triggerDownload(blob, filename);
      toast.showToast('已开始下载', 'success');
    } catch {
      // 后端导出未就绪 → 前端用已加载的数据拼一份，保证按钮永远有反应
      const csv = buildFallbackCsv(nodes, nickname);
      triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `打卡明细-${nickname}.csv`);
      toast.showToast('服务端导出尚未就绪，已用当前页面的数据生成 CSV', 'info');
    } finally {
      setExporting(false);
    }
  };

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2];
  }, []);

  return (
    <AppShell
      kind="parent"
      title={`${nickname} · 统计`}
      headerAction={
        <Button size="sm" variant="secondary" loading={exporting} onClick={() => void exportCsv()}>
          导出 CSV
        </Button>
      }
    >
      <div className="space-y-6">
        {allNotReady ? (
          <NotReadyState
            feature="统计仪表盘"
            description="统计接口还在上线中。目标、打卡记录都可以正常使用；数据接通后这里会自动出现。"
          />
        ) : (
          <>
            {/* ---------- 概览四宫格 ---------- */}
            {overviewQuery.isLoading ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            ) : overviewQuery.isError ? (
              <ErrorState
                message={toErrorMessage(overviewQuery.error)}
                onRetry={() => void overviewQuery.refetch()}
              />
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <StatTile
                  label="完成率"
                  value={
                    overview?.completionRate === null || overview?.completionRate === undefined
                      ? '—'
                      : `${Math.round(overview.completionRate * 100)}%`
                  }
                  hint={
                    overview?.scheduledCount !== undefined && overview?.doneCount !== undefined
                      ? `计划 ${overview.scheduledCount} 次 · 完成 ${overview.doneCount} 次`
                      : '统计区间内的计划完成比'
                  }
                />
                <StatTile
                  label="连续打卡"
                  value={`${overview?.streak ?? 0} 天`}
                  tone="emerald"
                  hint="只算按时完成；补卡不计入，非计划日不断连"
                />
                {/* 总投入时长 = **实际用时**的合计 → 走 `@fht/shared` 的
                    formatDuration（全项目唯一口径：45 秒写"45 秒"，不再显示成"0 分钟"）。
                    ⚠️ 已知问题（**不是**格式化的问题，也不在"只做显示格式"的范围内）：
                    后端 `/stats/overview` 返回的是 `duration.doneSeconds` /
                    `duration.doneMinutes`，而 `lib/api/stats.ts` 读的是 `totalMinutes`
                    （后端从不发这个键），所以这个格子目前恒为 0 —— 那是**字段对不上**。
                    同一处映射问题还影响本页的完成率 / 连续天数 / 漏打（后端给的是
                    `completion.*` / `streak.current` / `range`，与 web 期望的扁平形状不一致）。
                    格式这半边已经正确，映射单独修好之后这里立刻就对。 */}
                <StatTile
                  label="总投入时长"
                  value={formatDuration((overview?.totalMinutes ?? 0) * 60)}
                  tone="amber"
                  hint="由服务端时间戳累计，改本机时间不影响"
                />
                <StatTile
                  label="漏打 / 补卡"
                  value={`${overview?.missedCount ?? 0} / ${overview?.makeupCount ?? 0}`}
                  tone="slate"
                  hint="漏打可以在宽限期内补"
                />
              </div>
            )}

            {/* ---------- 趋势折线 ---------- */}
            <Card>
              <CardHeader
                title="趋势"
                description="完成率按「计划 vs 完成」算；时长与积分都来自服务端入账记录。"
                action={
                  <div className="flex flex-wrap items-end gap-2">
                    <Select
                      label="指标"
                      className="h-9 py-0 text-sm"
                      value={metric}
                      onChange={(e) => setMetric(e.target.value as TimeseriesMetric)}
                    >
                      {(Object.keys(METRIC_LABEL) as TimeseriesMetric[]).map((value) => (
                        <option key={value} value={value}>
                          {METRIC_LABEL[value]}
                        </option>
                      ))}
                    </Select>
                    <Select
                      label="粒度"
                      className="h-9 py-0 text-sm"
                      value={interval}
                      onChange={(e) => setInterval(e.target.value as TimeseriesInterval)}
                    >
                      {(Object.keys(INTERVAL_LABEL) as TimeseriesInterval[]).map((value) => (
                        <option key={value} value={value}>
                          {INTERVAL_LABEL[value]}
                        </option>
                      ))}
                    </Select>
                  </div>
                }
              />

              {seriesQuery.isLoading ? (
                <Skeleton className="h-52 w-full" />
              ) : seriesQuery.data?.notReady ? (
                <NotReadyState feature="趋势数据" compact />
              ) : seriesQuery.isError ? (
                <ErrorState
                  message={toErrorMessage(seriesQuery.error)}
                  onRetry={() => void seriesQuery.refetch()}
                  compact
                />
              ) : (
                <TimeSeriesChart points={series} metric={metric} height={220} />
              )}
            </Card>

            {/* ---------- 热力图 ---------- */}
            <Card>
              <CardHeader
                title="打卡热力图"
                description="一年视图，颜色深浅表示当天打了多少次卡。点任意方块看那一天的详情。"
                action={
                  <Select
                    label="年份"
                    className="h-9 py-0 text-sm"
                    value={String(year)}
                    onChange={(e) => setYear(Number(e.target.value))}
                  >
                    {years.map((value) => (
                      <option key={value} value={value}>
                        {value} 年
                      </option>
                    ))}
                  </Select>
                }
              />
              {heatmapQuery.isLoading ? (
                <Skeleton className="h-32 w-full" />
              ) : heatmapQuery.data?.notReady ? (
                <NotReadyState feature="热力图" compact />
              ) : heatmap.days.length === 0 ? (
                <EmptyState
                  icon="🗓️"
                  title={`${year} 年还没有打卡记录`}
                  description="孩子完成第一次打卡后，这里就会出现色块。"
                />
              ) : (
                <Heatmap year={year} days={heatmap.days} />
              )}
            </Card>

            {/* ---------- 目标进度树 ---------- */}
            <Card>
              <CardHeader
                title="目标进度树"
                description="父节点进度 = Σ(子节点已完成值) / Σ(子节点目标值)；没有量化目标的子节点按「1 次」计。"
              />
              {nodesQuery.isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-20 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : nodesQuery.data?.notReady ? (
                <NotReadyState feature="目标进度" compact />
              ) : nodesQuery.isError ? (
                <ErrorState
                  message={toErrorMessage(nodesQuery.error)}
                  onRetry={() => void nodesQuery.refetch()}
                  compact
                />
              ) : nodes.length === 0 ? (
                <EmptyState
                  icon="🎯"
                  title="还没有目标"
                  description="先给孩子定一个方向，进度才会出现在这里。"
                />
              ) : (
                <ul className="space-y-3">
                  {nodes.map((node) => (
                    <ProgressNodeRow key={node.id} node={node} />
                  ))}
                </ul>
              )}
            </Card>

            {/* ---------- 导出 ---------- */}
            <Card>
              <CardHeader
                title="明细导出"
                description="导出的是目标的进度明细；后端接口就绪后会直接下载服务端生成的完整打卡明细。"
              />
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" loading={exporting} onClick={() => void exportCsv()}>
                  下载 CSV
                </Button>
                <span className="self-center text-xs text-slate-400">
                  周报 / 月报、徽章、微信提醒属于下一阶段，本批不含。
                </span>
              </div>
            </Card>
          </>
        )}

        <p className={cn('text-xs text-slate-400')}>
          时间口径：所有「今天」「当日结算」都按家庭时区（Asia/Shanghai）计算；
          实际时长由服务端时间戳累计，改客户端系统时间不会影响统计。
        </p>
      </div>
    </AppShell>
  );
}
